import React from 'react';
import dynamic from 'next/dynamic';
import SEO from '@c/SEO';
import Button from '@ui/Button';
import { DefaultLayout } from '@c/layouts';

// SearchBox is not SSR-safe (uses browser APIs); dynamic import matches Navbar pattern.
const SearchBox = dynamic(() => import('@ui/SearchBox'), { ssr: false });

// Standard section container used across the site (matches MasterProductsPageV2).
const SECTION = 'max-w-default mx-auto px-[2rem] md:px-[4rem] xl:px-default-padding-x py-[3rem]';

export default function GearValuesPage() {
  return (
    <>
      <SEO
        title="What's My Gear Worth? Used Camera Gear Values"
        description="Search any camera or lens and see its real used market value, built from tens of thousands of actual sales. Then sell it or shop below market on GearFocus."
        canonicalUrl="https://gearfocus.com/m/gear-values"
      />

      {/* ── Hero (light gray) ────────────────────────────────────────────── */}
      <section className="w-full bg-[#f4f2f2]">
        <div className={SECTION}>
          <div className="mx-auto max-w-3xl text-center">
            <h1 className="text-[2.8rem] sm:text-[3.6rem] font-extrabold text-black leading-tight">
              Your Gear Has a Number.
            </h1>
            <p className="mt-[1.6rem] text-[1.6rem] leading-[2.4rem] text-gray-600">
              Every camera and lens you own has a real, current market value, built from tens of
              thousands of actual sales, not a guess and not a retail sticker. Search yours below
              and see what it is worth.
            </p>

            {/* Search box — valuationMode shows only GearMaster master results */}
            {/*
              Wrapper adds visual definition so the white input reads clearly on
              the tinted hero. rounded-[1rem] matches Input's border-radius exactly
              (InputStyles base: 'rounded-[1rem]'). No overflow-hidden so the
              autocomplete dropdown is never clipped.
            */}
            <div className="mt-[2.4rem]">
              <div className="rounded-[1rem] shadow-md ring-1 ring-gray-200 w-full">
                <SearchBox valuationMode />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── How It Works (white) ─────────────────────────────────────────── */}
      <section className="w-full bg-white">
        <div className={SECTION}>
          <h2 className="text-center text-[2rem] sm:text-[2.4rem] font-extrabold text-black mb-[3rem]">
            How It Works
          </h2>
          <ol className="grid gap-[2.4rem] sm:grid-cols-3 max-w-4xl mx-auto">
            <li className="flex flex-col items-center text-center gap-[1.2rem]">
              <span className="flex h-[4.8rem] w-[4.8rem] items-center justify-center rounded-full bg-brand-secondary text-white text-[2rem] font-bold">
                1
              </span>
              <h3 className="text-[1.8rem] font-extrabold text-black">Search your gear.</h3>
              <p className="text-[1.4rem] leading-[2.1rem] text-gray-600">
                Find your exact camera or lens.
              </p>
            </li>
            <li className="flex flex-col items-center text-center gap-[1.2rem]">
              <span className="flex h-[4.8rem] w-[4.8rem] items-center justify-center rounded-full bg-brand-secondary text-white text-[2rem] font-bold">
                2
              </span>
              <h3 className="text-[1.8rem] font-extrabold text-black">See its market value.</h3>
              <p className="text-[1.4rem] leading-[2.1rem] text-gray-600">
                A range by condition, built from tens of thousands of real sales.
              </p>
            </li>
            <li className="flex flex-col items-center text-center gap-[1.2rem]">
              <span className="flex h-[4.8rem] w-[4.8rem] items-center justify-center rounded-full bg-brand-secondary text-white text-[2rem] font-bold">
                3
              </span>
              <h3 className="text-[1.8rem] font-extrabold text-black">Sell it or shop it.</h3>
              <p className="text-[1.4rem] leading-[2.1rem] text-gray-600">
                List in minutes, or buy below market from verified sellers.
              </p>
            </li>
          </ol>
        </div>
      </section>

      {/* ── Trust Line (light gray) ──────────────────────────────────────── */}
      <section className="w-full bg-[#f4f2f2]">
        <div className="max-w-default mx-auto px-[2rem] md:px-[4rem] xl:px-default-padding-x py-[2rem]">
          <p className="text-center text-[1.4rem] text-gray-600">
            Tens of thousands of sales analyzed
            <span className="mx-[1.2rem] text-brand-primary select-none" aria-hidden="true">·</span>
            5.0 on Google
            <span className="mx-[1.2rem] text-brand-primary select-none" aria-hidden="true">·</span>
            Verified sellers
            <span className="mx-[1.2rem] text-brand-primary select-none" aria-hidden="true">·</span>
            48-hour buyer protection
          </p>
        </div>
      </section>

      {/* ── CTA Buttons (white) ──────────────────────────────────────────── */}
      <section className="w-full bg-white">
        <div className={SECTION}>
          <div className="mx-auto max-w-xl flex flex-col items-center gap-[1.6rem] sm:flex-row sm:justify-center">
            <Button type="primary" href="/sell" text="List Your Gear" />
            <Button type="secondary" href="/products" text="Shop on GearFocus" />
          </div>
        </div>
      </section>
    </>
  );
}

GearValuesPage.getLayout = function getLayout(page: React.ReactElement) {
  return <DefaultLayout>{page}</DefaultLayout>;
};
